import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import {
  getUserSettings,
  updateUserSettings,
  type UserSettings,
} from "@app/services/userSettingsApi";
import { getAppConfig, type AppConfig } from "@app/services/appConfigApi";
import { logger } from "@app/services/logger";
import { PolicyAcceptanceCheckbox } from "@app/components/policy/PolicyAcceptanceCheckbox";
import { getPolicyStatus } from "@app/services/policyApi";
import "./userTradingSystem.css";

export function UserTradingSystemTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [isToggling, setIsToggling] = useState(false);
  const [showPolicyCheckbox, setShowPolicyCheckbox] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);

  const {
    data: settings,
    isLoading: settingsLoading,
    error: settingsError,
  } = useQuery<UserSettings>({
    queryKey: ["userSettings"],
    queryFn: getUserSettings,
  });

  const {
    data: appConfig,
    isLoading: configLoading,
    error: configError,
  } = useQuery<AppConfig>({
    queryKey: ["appConfig"],
    queryFn: getAppConfig,
  });

  const { data: policyStatus } = useQuery({
    queryKey: ["user", "policy", "status"],
    queryFn: getPolicyStatus,
  });

  // Check if risk policy is accepted
  const hasAcceptedRisk = policyStatus?.policies?.risk?.accepted ?? false;

  const updateMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      updateUserSettings({ trading_enabled: enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userSettings"] });
      queryClient.invalidateQueries({ queryKey: ["appConfig"] });
      setIsToggling(false);
    },
    onError: (error: any) => {
      logger.error("Failed to update trading enabled", error);
      alert(
        `Failed to update trading status: ${error.response?.data?.detail || error.message}`,
      );
      setIsToggling(false);
    },
  });

  const handleToggle = () => {
    if (!settings) return;

    const newValue = !settings.trading_enabled;

    // Check if user is enabling trading for the first time and hasn't accepted risk policy
    if (newValue && !hasAcceptedRisk) {
      setShowPolicyCheckbox(true);
      setPolicyAccepted(false);
      return;
    }

    const confirmMessage = newValue
      ? "Enable trading for your account? This will allow the system to place orders on your account using your own API key."
      : "Disable trading for your account? This will prevent the system from placing any orders on your account.";

    if (confirm(confirmMessage)) {
      setIsToggling(true);
      updateMutation.mutate(newValue);
    }
  };

  const handlePolicyAccepted = () => {
    setPolicyAccepted(true);
    setShowPolicyCheckbox(false);
    // Proceed with enabling trading after policy acceptance
    if (settings) {
      setIsToggling(true);
      updateMutation.mutate(true);
    }
  };

  const handlePolicyRejected = () => {
    setShowPolicyCheckbox(false);
    setPolicyAccepted(false);
  };

  const isLoading = settingsLoading || configLoading;
  const error = settingsError || configError;

  if (isLoading) {
    return (
      <div className="user-trading-system-root">
        <SectionHeader
          title="Trading System"
          subtitle="Control your personal trading settings and view your trading statistics"
        />
        <Card className="user-trading-system-card">
          <Skeleton count={3} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-trading-system-root">
        <SectionHeader
          title="Trading System"
          subtitle="Control your personal trading settings and view your trading statistics"
        />
        <Card className="user-trading-system-card">
          <div className="user-trading-system-error">
            Error loading trading settings:{" "}
            {error instanceof Error ? error.message : "Unknown error"}
          </div>
        </Card>
      </div>
    );
  }

  const tradingEnabled = settings?.trading_enabled ?? false;
  const tradesUsedToday = appConfig?.trades_used_today ?? 0;
  const planCode = appConfig?.plan_code ?? "N/A";
  const dailyCap = appConfig?.plan_code
    ? appConfig.plan_code === "STANDARD"
      ? 10
      : appConfig.plan_code === "PRO"
        ? 50
        : 0
    : 0;

  return (
    <div className="user-trading-system-root">
      <SectionHeader
        title="Trading System"
        subtitle="Control your personal trading settings and view your trading statistics"
      />

      <Card className="user-trading-system-card">
        <div className="user-trading-system-section">
          <div className="user-trading-system-toggle-section">
            <div className="user-trading-system-toggle-header">
              <h4>Personal Trading Control</h4>
              <p className="user-trading-system-description">
                Enable or disable trading for your account. When disabled, the
                system will not place any orders on your account, even if
                global trading is enabled.
              </p>
            </div>
            <div className="user-trading-system-toggle-control">
              <div className="user-trading-system-toggle-label">
                <span className="user-trading-system-toggle-label-text">
                  Trading Enabled
                </span>
                <span
                  className={`user-trading-system-status-badge ${
                    tradingEnabled
                      ? "user-trading-system-status-enabled"
                      : "user-trading-system-status-disabled"
                  }`}
                >
                  {tradingEnabled ? "Enabled" : "Disabled"}
                </span>
              </div>
              <Button
                type="button"
                variant={tradingEnabled ? "danger" : "primary"}
                onClick={handleToggle}
                disabled={isToggling || updateMutation.isPending}
              >
                {isToggling || updateMutation.isPending
                  ? "Updating..."
                  : tradingEnabled
                    ? "Disable Trading"
                    : "Enable Trading"}
              </Button>

              {showPolicyCheckbox && (
                <div className="user-trading-system-policy">
                  <PolicyAcceptanceCheckbox
                    required={true}
                    showStatus={false}
                    onAccepted={handlePolicyAccepted}
                    onRejected={handlePolicyRejected}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="user-trading-system-divider" />

        <div className="user-trading-system-section">
          <h4 className="user-trading-system-stats-title">
            Your Trading Statistics
          </h4>
          <div className="user-trading-system-stats-grid">
            <div className="user-trading-system-stat-card">
              <div className="user-trading-system-stat-label">Plan</div>
              <div className="user-trading-system-stat-value">{planCode}</div>
            </div>
            <div className="user-trading-system-stat-card">
              <div className="user-trading-system-stat-label">
                Trades Used Today
              </div>
              <div className="user-trading-system-stat-value">
                {tradesUsedToday}
              </div>
            </div>
            <div className="user-trading-system-stat-card">
              <div className="user-trading-system-stat-label">Daily Cap</div>
              <div className="user-trading-system-stat-value">
                {dailyCap > 0 ? dailyCap : "Unlimited"}
              </div>
            </div>
            <div className="user-trading-system-stat-card">
              <div className="user-trading-system-stat-label">Remaining</div>
              <div className="user-trading-system-stat-value">
                {dailyCap > 0
                  ? Math.max(0, dailyCap - tradesUsedToday)
                  : "Unlimited"}
              </div>
            </div>
          </div>
        </div>

        {!tradingEnabled && (
          <div className="user-trading-system-warning">
            <strong>Note:</strong> Trading is currently disabled for your
            account. Enable it above to allow the system to place orders on
            your account.
          </div>
        )}
      </Card>
    </div>
  );
}

